import { type TFunction } from 'i18next';
import {
  calendarDateIn,
  type CreateRaidInput,
  DECISION_OTHER_KEY,
  type DecisionQuestionView,
  type DecisionSheetView,
  type RaidItemView,
  splitDecisionKeys,
} from '@apm/contracts';
import { raidApi } from '../raid/api';
import { decisionSheetApi } from './api';

/**
 * "Ghi vào RAID" — APM-DSHEET-f, plan §6.2. A decided question becomes a RAID
 * `DECISION` through the existing `POST /projects/:id/raid`; the sheet then
 * remembers which item and which answer revision it wrote
 * (`POST /decision-sheets/:id/raid-recorded`).
 *
 * This is a person's action, not AI output — rule #6 (`ai_suggested`) does not
 * apply.
 */

/** `CreateRaidInput.title` — `.max(300)` in `packages/contracts/src/raid.ts`. */
export const RAID_TITLE_MAX = 300;
/** `CreateRaidInput.rationale` — `.max(5_000)`. */
const RAID_RATIONALE_MAX = 5_000;

export interface RaidDecisionLabels {
  /** Name of the "Other" answer (`X`). */
  readonly other: string;
  readonly note: (note: string) => string;
  readonly fromSheet: (title: string) => string;
  /** Re-recording a changed answer: "Replaces decision D-6". */
  readonly replaces: (ref: string) => string;
  /** Answer from an outsider via a public link: "Người trả lời: Nguyễn A (a@x.com, qua link)". */
  readonly respondent: (name: string, email: string) => string;
}

export function raidDecisionLabels(t: TFunction): RaidDecisionLabels {
  return {
    other: t('decisionSheet.other'),
    note: (note) => t('decisionSheet.raid.note', { note }),
    fromSheet: (title) => t('decisionSheet.raid.fromSheet', { title }),
    replaces: (ref) => t('decisionSheet.raid.replaces', { ref }),
    respondent: (name, email) => t('decisionSheet.raid.respondent', { name, email }),
  };
}

/** Cut to `max` UTF-16 units (what Zod's `.max` counts) with an ellipsis, never splitting a surrogate pair. */
export function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).replace(/[\uD800-\uDBFF]$/, '')}…`;
}

const oneLine = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * RAID needs an owner, and the owner of a decision is who decided it. An
 * outsider who answered via a public link has no account (`answeredBy` null)
 * ⇒ the person recording owns the RAID item and the rationale names the
 * respondent (plan §11.3) — so that answer is recordable too, unlike one whose
 * decider is simply unknown.
 */
export function isRecordable(question: DecisionQuestionView): boolean {
  const answer = question.answer;
  return answer !== null && (answer.answeredBy !== null || Boolean(answer.respondent));
}

export type RaidRecordState = 'new' | 'recorded' | 'changed';

/** `changed` = recorded, then the answer moved on (`answerRev ≠ raidAnswerRev`). */
export function raidRecordState(question: DecisionQuestionView): RaidRecordState {
  if (!question.raidItemId) return 'new';
  return question.raidAnswerRev === question.answerRev ? 'recorded' : 'changed';
}

/** Ticked when the dialog opens: what is not in RAID yet, or no longer matches it. */
export function defaultRaidSelection(questions: readonly DecisionQuestionView[]): Set<string> {
  return new Set(
    questions
      .filter((question) => isRecordable(question) && raidRecordState(question) !== 'recorded')
      .map((question) => question.id),
  );
}

/**
 * One decided question ⇒ the input of a RAID `DECISION` (plan §6.2):
 *
 * - `title` = `"<code> <question>: <key>. <option>"`, clipped to the RAID cap.
 *   For "Other" (`X`) the option name says nothing, so the **note** stands in.
 * - `rationale` = option description (or "Other") + note + "From sheet …"
 *   (+ "Replaces decision <old>" when re-recording a changed answer — the old
 *   RAID row is never edited: it was a real decision at the time).
 * - `ownerId` = who decided (an outsider via link ⇒ the recorder, and the
 *   rationale names the respondent — plan §11.3); `decidedAt` = the
 *   decision's **calendar day in the org timezone** (RAID stores a `DATE`; the
 *   UTC day is wrong 00:00–07:00 at +07).
 */
export function raidDecisionFromQuestion(
  sheet: Pick<DecisionSheetView, 'title'>,
  question: DecisionQuestionView,
  previousRaid: string | undefined,
  context: RaidRecordContext,
): CreateRaidInput {
  const { answer } = question;
  // Outsider via link ⇒ the recorder owns it (plan §11.3); otherwise who decided.
  const ownerId =
    answer?.answeredBy?.id ?? (answer?.respondent ? (context.recorderId ?? null) : null);
  if (!answer || !ownerId) {
    throw new Error(`Question ${question.code} has no decider — cannot record it in RAID`);
  }
  const { labels, timezone } = context;
  const note = answer.note.trim();
  /*
    Câu chọn nhiều (APM-DSX-multi): `answer.key` là "A,C,X". Tiêu đề liệt kê
    từng lựa chọn ("A,C,X. Email; SMS; <ghi chú>"), mô tả gộp chi tiết của các
    phương án đã chọn. Câu chọn một đi đúng đường cũ.
  */
  const keys = splitDecisionKeys(answer.key);
  const chosen = keys.map((key) => ({
    key,
    option: question.options.find((candidate) => candidate.key === key),
  }));
  const option = chosen.length === 1 ? chosen[0]!.option : undefined;
  const isOther = keys.length === 1 && keys[0] === DECISION_OTHER_KEY;
  const nameOf = (entry: (typeof chosen)[number]): string =>
    entry.key === DECISION_OTHER_KEY
      ? oneLine(note) || labels.other
      : (entry.option?.title ?? entry.key);

  const choice = chosen.map(nameOf).join('; ');
  const title = clip(
    `${question.code} ${oneLine(question.title)}: ${answer.key}. ${choice}`,
    RAID_TITLE_MAX,
  );

  const description =
    chosen.length > 1
      ? chosen
          .map((entry) =>
            entry.key === DECISION_OTHER_KEY
              ? labels.other
              : `${entry.key}. ${entry.option?.detail?.trim() || entry.option?.title || ''}`,
          )
          .join('\n')
      : isOther
        ? labels.other
        : option?.detail?.trim() || option?.title || '';
  const rationale = clip(
    [
      description,
      note ? labels.note(note) : '',
      answer.respondent && !answer.answeredBy
        ? labels.respondent(answer.respondent.name, answer.respondent.email)
        : '',
      labels.fromSheet(sheet.title),
      previousRaid ? labels.replaces(previousRaid) : '',
    ]
      .filter((part) => part !== '')
      .join('\n\n'),
    RAID_RATIONALE_MAX,
  );

  return {
    type: 'DECISION',
    title,
    ownerId,
    decidedAt: calendarDateIn(new Date(answer.answeredAt), timezone),
    rationale,
  };
}

export interface RaidRecordContext {
  readonly labels: RaidDecisionLabels;
  readonly timezone: string;
  /** Who is recording — owner of an outsider's answer (APM-DSX-b). */
  readonly recorderId?: string | null | undefined;
}

export interface RaidRecordOutcome {
  readonly recorded: readonly {
    readonly question: DecisionQuestionView;
    readonly item: RaidItemView;
  }[];
  readonly failed: readonly { readonly question: DecisionQuestionView; readonly error: unknown }[];
  /** RAID items exist but `raid-recorded` failed — the sheet does not know them yet. */
  readonly syncError: unknown;
}

/**
 * Create the RAID items one by one (sequential: one failure must not take the
 * others down, and the error must name its question), then tell the sheet
 * about **the ones that succeeded** — question 5 of 10 failing still records
 * 1–4 and 6–10 (sprint plan APM-DSHEET-f "Lưu ý").
 *
 * ⚠️ Two modules, two transactions: if `raid-recorded` fails, the RAID items
 * stay and the sheet still shows the questions as unrecorded. `syncError`
 * reports it; ticking them again would create duplicates, so the dialog says
 * so instead of retrying silently.
 */
export async function recordDecisionsToRaid(
  sheet: Pick<DecisionSheetView, 'id' | 'projectId' | 'title'>,
  questions: readonly DecisionQuestionView[],
  previousRaidRefs: ReadonlyMap<string, string>,
  context: RaidRecordContext,
): Promise<RaidRecordOutcome> {
  const recorded: { question: DecisionQuestionView; item: RaidItemView }[] = [];
  const failed: { question: DecisionQuestionView; error: unknown }[] = [];

  for (const question of questions) {
    try {
      const previous = question.raidItemId ? previousRaidRefs.get(question.raidItemId) : undefined;
      const input = raidDecisionFromQuestion(sheet, question, previous, context);
      recorded.push({ question, item: await raidApi.create(sheet.projectId, input) });
    } catch (error) {
      failed.push({ question, error });
    }
  }

  let syncError: unknown = null;
  if (recorded.length > 0) {
    try {
      await decisionSheetApi.recordRaid(sheet.id, {
        items: recorded.map(({ question, item }) => ({
          questionId: question.id,
          raidItemId: item.id,
          rev: question.answerRev,
        })),
      });
    } catch (error) {
      syncError = error;
    }
  }
  return { recorded, failed, syncError };
}
